import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

// Temas predefinidos
const themes = {
  dark: {
    name: 'Escuro',
    background: '#09090b',
    cardBackground: 'rgba(0, 0, 0, 0.6)',
    textPrimary: '#ffffff',
    textSecondary: '#71717a',
    border: 'rgba(255, 255, 255, 0.1)',
  },
  light: {
    name: 'Claro',
    background: '#f0f0f2',
    cardBackground: '#ffffff',
    textPrimary: '#0a0a0a',
    textSecondary: '#3f3f46',
    border: 'rgba(0, 0, 0, 0.2)',
  },
  midnight: {
    name: 'Meia-Noite',
    background: '#0f172a',
    cardBackground: 'rgba(30, 41, 59, 0.8)',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    border: 'rgba(148, 163, 184, 0.2)',
  }
};

// Paletas de cores predefinidas
const colorPalettes = {
  gym: {
    name: 'Alvo Premium',
    primary: '#22C55E',
    secondary: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
  },
  ocean: {
    name: 'Oceano',
    primary: '#0EA5E9',
    secondary: '#06B6D4',
    danger: '#F43F5E',
    warning: '#F59E0B',
  },
  sunset: {
    name: 'Pôr do Sol',
    primary: '#F97316',
    secondary: '#EC4899',
    danger: '#EF4444',
    warning: '#EAB308',
  },
  forest: {
    name: 'Floresta',
    primary: '#22C55E',
    secondary: '#14B8A6',
    danger: '#DC2626',
    warning: '#CA8A04',
  },
  purple: {
    name: 'Roxo Real',
    primary: '#8B5CF6',
    secondary: '#A855F7',
    danger: '#E11D48',
    warning: '#D97706',
  },
  custom: {
    name: 'Personalizado',
    primary: '#22C55E',
    secondary: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
  }
};

// Configurações padrão
const defaultSettings = {
  // Aparência
  theme: 'dark',
  colorPalette: 'gym',
  customColors: {
    primary: '#22C55E',
    secondary: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
  },
  
  // Dashboard
  dashboardLayout: 'default', // default, compact, expanded
  showWelcomeMessage: true,
  defaultPeriod: 'current', // current, previous, custom
  cardsToShow: {
    revenue: true,
    expenses: true,
    profit: true,
    pendingBills: true,
    monthlyTrend: true,
    expensesByCategory: true,
    revenueBySource: true,
    goals: true,
  },
  
  // Formato de números
  currency: 'BRL',
  currencySymbol: 'R$',
  decimalPlaces: 2,
  thousandsSeparator: '.',
  decimalSeparator: ',',
  
  // Metas
  defaultGoalDirection: 'progressiva',
  showGoalPercentage: true,
  goalColorCoding: true,
  
  // Notificações
  notifications: {
    billReminders: true,
    goalAlerts: true,
    monthlyReport: true,
    reminderDaysBefore: 3,
  },
  
  // Gráficos
  chartAnimations: true,
  chartStyle: 'modern', // modern, classic, minimal
  
  // Sidebar
  sidebarCollapsed: false,
  
  // Acessibilidade
  fontSize: 'medium', // small, medium, large
  highContrast: false,
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('gymfi_settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  // Salvar no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('gymfi_settings', JSON.stringify(settings));
    applyTheme(settings);
  }, [settings]);

  // Aplicar tema no documento
  const applyTheme = (currentSettings) => {
    const root = document.documentElement;
    const body = document.body;
    const theme = themes[currentSettings.theme] || themes.dark;
    const palette = currentSettings.colorPalette === 'custom' 
      ? currentSettings.customColors 
      : colorPalettes[currentSettings.colorPalette] || colorPalettes.gym;

    // Aplicar variáveis CSS do tema
    root.style.setProperty('--app-background', theme.background);
    root.style.setProperty('--app-card-bg', theme.cardBackground);
    root.style.setProperty('--app-text-primary', theme.textPrimary);
    root.style.setProperty('--app-text-secondary', theme.textSecondary);
    root.style.setProperty('--app-border', theme.border);
    
    // Aplicar variáveis CSS das cores
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-secondary', palette.secondary);
    root.style.setProperty('--color-danger', palette.danger);
    root.style.setProperty('--color-warning', palette.warning);

    // Aplicar background diretamente no body
    body.style.backgroundColor = theme.background;
    body.style.color = theme.textPrimary;

    // Classe do tema
    root.classList.remove('theme-dark', 'theme-light', 'theme-midnight');
    root.classList.add(`theme-${currentSettings.theme}`);

    // Font size
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${currentSettings.fontSize}`);

    // High contrast
    if (currentSettings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  };

  const updateSettings = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateNestedSettings = (parentKey, childKey, value) => {
    setSettings(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const getCurrentTheme = () => themes[settings.theme] || themes.dark;
  
  const getCurrentPalette = () => {
    if (settings.colorPalette === 'custom') {
      return settings.customColors;
    }
    return colorPalettes[settings.colorPalette] || colorPalettes.gym;
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSettings,
      updateNestedSettings,
      resetSettings,
      themes,
      colorPalettes,
      getCurrentTheme,
      getCurrentPalette,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
